import { useMemo, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, Clock, Clock2, Clock3, Sun, Sunrise, Timer } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { getSnoozePresets } from "@/lib/thread-status";
import { cn } from "@/lib/utils";

const PRESET_ICONS = { hour: Timer, hours2: Clock2, hours3: Clock3, afternoon: Sun, tomorrow: Sunrise };

/**
 * Snooze preset popover (design 02-thread-tasks): In 1/2/3 hours · This afternoon 2:00 PM ·
 * Tomorrow 9:00 AM · Pick date & time…, with the footer note explaining the Pending move.
 * Confirming calls `onSnooze(untilDate)`. The trigger is supplied by the caller (header
 * button, panel row, board drop) via `children`; pass `open`/`onOpenChange` to control it.
 */
export function SnoozeMenu({ onSnooze, children, open, onOpenChange, align = "end" }) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = typeof open === "boolean";
  const actualOpen = isControlled ? open : uncontrolledOpen;
  const setOpen = (next) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // "Pick date & time…" sub-view state.
  const [picking, setPicking] = useState(false);
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedTime, setPickedTime] = useState("09:00");

  const presets = useMemo(() => (actualOpen ? getSnoozePresets(new Date()) : []), [actualOpen]);

  const choose = (date) => {
    setOpen(false);
    setPicking(false);
    setPickedDate(null);
    onSnooze?.(date);
  };

  const confirmPicked = () => {
    if (!pickedDate) return;
    const [h, min] = pickedTime.split(":").map((p) => Number(p));
    const at = new Date(pickedDate);
    at.setHours(Number.isFinite(h) ? h : 9, Number.isFinite(min) ? min : 0, 0, 0);
    if (at.getTime() <= Date.now()) return;
    choose(at);
  };

  return (
    <Popover
      open={actualOpen}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setPicking(false);
          setPickedDate(null);
        }
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {/* The date picker is wider than the preset list: let it size to the calendar's
          natural width so the last weekday column isn't clipped. */}
      <PopoverContent align={align} className={cn("overflow-hidden p-0", picking ? "w-auto" : "w-[240px]")}>
        {!picking ? (
          <>
            <p className="border-b border-border px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Remind me…
            </p>
            {presets.map((preset) => {
              const Icon = PRESET_ICONS[preset.id] || Clock;
              const highlighted = preset.id === "afternoon";
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => choose(preset.at)}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    highlighted
                      ? "bg-amber-50 font-semibold text-amber-800 hover:bg-amber-100/80 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
                      : "text-foreground hover:bg-muted/70",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-3.5 w-3.5 shrink-0",
                      highlighted ? "text-amber-500 dark:text-amber-400" : "text-muted-foreground",
                    )}
                    strokeWidth={2.25}
                    aria-hidden
                  />
                  {preset.label}
                  <span
                    className={cn(
                      "ml-auto font-mono text-[10px]",
                      highlighted ? "text-amber-600 dark:text-amber-300" : "text-muted-foreground/80",
                    )}
                  >
                    {preset.timeLabel}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="flex w-full items-center gap-2.5 border-t border-border px-3 py-2 text-left text-xs text-foreground transition hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={2.25} aria-hidden />
              Pick date &amp; time…
            </button>
            <p className="border-t border-border bg-muted/50 px-3 py-2 text-[10px] leading-snug text-muted-foreground">
              Moves to{" "}
              <span className="font-semibold text-amber-700 dark:text-amber-300">Pending</span> and
              leaves your queue. Returns when due — or sooner if the patient replies.
            </p>
          </>
        ) : (
          <div className="p-2">
            <button
              type="button"
              onClick={() => setPicking(false)}
              className="mb-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft className="h-3 w-3" aria-hidden />
              Presets
            </button>
            <Calendar
              mode="single"
              selected={pickedDate ?? undefined}
              onSelect={(d) => setPickedDate(d ?? null)}
              disabled={{ before: new Date() }}
              autoFocus={false}
            />
            <div className="flex items-center gap-2 border-t border-border pt-2">
              <input
                type="time"
                value={pickedTime}
                onChange={(e) => setPickedTime(e.target.value || "09:00")}
                aria-label="Snooze time"
                className="h-7 flex-1 rounded-md border border-border bg-background px-2 font-mono text-[11px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <button
                type="button"
                disabled={!pickedDate}
                onClick={confirmPicked}
                className="inline-flex h-7 items-center rounded-md bg-amber-500 px-2.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-600 dark:hover:bg-amber-500"
              >
                Snooze
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
